// Purpose: Bridge web social-login intents to Flutter native handlers when running inside the app webview.
// Mistake: unhandled promise rejection surfaced when native bridge rejected (e.g., LINE cancel); normalize bridge errors to result objects.
// Mistake: synchronous bridge detection raced with late injection on non-local builds; wait briefly before fallback.
// Mistake: postMessage-only fallback then LIFF navigate caused LINE web flash on dev; retry bridge after postMessage before returning null.
// Mistake: web OAuth click waited multi-second bridge retries; if bridge object is absent, fail fast to web redirect path.
type SocialProvider = "google" | "apple" | "kakao" | "line";

export interface NativeSocialLoginResult {
    ok: boolean;
    provider?: SocialProvider | string;
    jwt?: string;
    isNewUser?: boolean;
    errorCode?: string;
    message?: string;
}

type SocialFlow = "signup" | "login";

const getFlutterBridge = () => {
    return (window as any)?.flutter_inappwebview;
};

const waitForFlutterBridge = async (
    timeoutMs = 1800,
    stepMs = 120
): Promise<any | null> => {
    const start = Date.now();
    let bridge = getFlutterBridge();
    while (!bridge?.callHandler && Date.now() - start < timeoutMs) {
        await new Promise((resolve) => window.setTimeout(resolve, stepMs));
        bridge = getFlutterBridge();
    }
    return bridge?.callHandler ? bridge : null;
};

export const isNativeSocialBridgeAvailable = (): boolean => {
    const bridge = getFlutterBridge();
    return typeof bridge?.callHandler === "function";
};

export const requestNativeSocialLogin = async (
    provider: SocialProvider,
    flow: SocialFlow = "login"
): Promise<NativeSocialLoginResult | null> => {
    const payload = { action: "login", provider, flow };

    const invokeBridge = async (bridge: any): Promise<NativeSocialLoginResult | null> => {
        if (!bridge?.callHandler) {
            return null;
        }
        try {
            const result = await bridge.callHandler("socialLogin", payload);
            return (result || null) as NativeSocialLoginResult | null;
        } catch (error: any) {
            const message = String(error?.message || error || "");
            const upperMessage = message.toUpperCase();
            const isCancel =
                upperMessage.includes("PLATFORMEXCEPTION(CANCEL") ||
                upperMessage.includes("PLATFORMEXCEPTION(3003") ||
                upperMessage.includes("USER CANCELLED") ||
                upperMessage.includes("INTERRUPTED THE LOGIN PROCESS");
            return {
                ok: false,
                provider,
                errorCode: isCancel ? "CANCELLED" : "BRIDGE_ERROR",
                message: isCancel ? "Login cancelled" : message || "Native bridge call failed",
            };
        }
    };

    const initialBridge = getFlutterBridge();
    if (!initialBridge) {
        // Pure web: skip long bridge retries and immediately continue with web OAuth redirect.
        return null;
    }

    let bridge = isNativeSocialBridgeAvailable()
        ? initialBridge
        : await waitForFlutterBridge(1200, 80);

    let result = await invokeBridge(bridge);
    if (result !== null) {
        return result;
    }

    window.postMessage(payload, "*");

    bridge = await waitForFlutterBridge(900, 60);
    result = await invokeBridge(bridge);
    if (result !== null) {
        return result;
    }

    return null;
};
